<template>
  <div class="fuel-efficiency-chart rounded-lg border border-gray-200 p-4">
    <!-- Header -->
    <div class="flex justify-between items-center mb-4">
      <div>
        <h3 class="text-lg font-semibold text-gray-900">
          {{ $t('FUEL_EFFICIENCY') }}
        </h3>
        <p class="text-sm text-gray-500">
          {{ $t('FUEL_CONSUMPTION_COST_EFFICIENCY') }}
        </p>
      </div>
      <!-- <el-button
        type="text"
        class="text-blue-600 hover:text-blue-800 focus-within:outline-none"
        @click="showAllSeries"
      >
        {{ $t('VIEW_ALL') }}
      </el-button> -->
    </div>

    <!-- Chart -->
    <div class="chart-container">
      <apexchart
        ref="chart"
        v-if="chartOptions && chartSeries.length"
        type="line"
        height="235"
        :options="chartOptions"
        :series="chartSeries"
      />
      <div v-else class="flex items-center justify-center h-64 text-gray-500">
        {{ $t('NO_DATA') }}
      </div>
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'

export default {
  name: 'FuelEfficiencyChart',
  components: { apexchart: VueApexCharts },
  props: {
    // expects: { results: [ {start_bucket_date, end_bucket_date, fleet_avg_fuel_amount_consumption_rate, cost_per_km, fleet_total_distance_meter}, ... ] }
    data: { type: Object, default: () => ({ results: [] }) },
  },
  computed: {
    labels() {
      if (!this.data || !this.data.results || !this.data.results.length)
        return []
      return this.data.results.map(function (r) {
        // Format dates to be more compact and readable
        const startDate = new Date(r.start_bucket_date)
        const endDate = new Date(r.end_bucket_date)

        // Use shorter format: MM/DD
        const startFormatted = startDate.toLocaleDateString('fa-IR', {
          month: '2-digit',
          day: '2-digit',
        })
        const endFormatted = endDate.toLocaleDateString('fa-IR', {
          month: '2-digit',
          day: '2-digit',
        })

        return startFormatted + ' - ' + endFormatted
      })
    },
    chartSeries() {
      if (!this.data || !this.data.results || !this.data.results.length)
        return []
      var res = this.data.results
      return [
        {
          name: this.$t('AVERAGE_FUEL_CONSUMPTION'),
          type: 'column',
          data: res.map(function (r) {
            return Number(r.fleet_avg_fuel_amount_consumption_rate || 0)
          }),
        },
        {
          name: this.$t('COST_PER_KM') + ' (' + this.$t('TOMAN') + ')',
          type: 'column',
          data: res.map(function (r) {
            return Number(
              (r.fleet_avg_fuel_cost_per_meter / 10).toFixed(1) || 0
            )
          }),
        },
        {
          name: this.$t('TOTAL_KM'),
          type: 'line',
          data: res.map(function (r) {
            return Number((r.fleet_total_distance_meter / 1000).toFixed(1) || 0)
          }),
        },
      ]
    },
    chartOptions() {
      if (!this.data || !this.data.results || !this.data.results.length)
        return null

      var self = this
      return {
        chart: {
          height: 300,
          type: 'line',
          stacked: false,
          toolbar: { show: false },
          animations: { enabled: true, easing: 'easeinout', speed: 500 },
          events: {
            legendClick: function (chartContext, seriesIndex, config) {
              // Toggle the clicked series (show if hidden, hide if shown)
              var seriesArr =
                config && config.w && config.w.config && config.w.config.series
                  ? config.w.config.series
                  : config && config.config && config.config.series
                  ? config.config.series
                  : []

              if (seriesArr[seriesIndex]) {
                var seriesName = seriesArr[seriesIndex].name
                var isVisible = chartContext.w.globals.series[seriesIndex].some(
                  (val) => val !== null
                )

                if (isVisible) {
                  chartContext.hideSeries(seriesName)
                } else {
                  chartContext.showSeries(seriesName)
                }
              }
              return false
            },
          },
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '60%',
            borderRadius: 2,
          },
        },
        dataLabels: { enabled: false },
        stroke: {
          width: [0, 0, 2],
          curve: 'smooth',
        },
        xaxis: {
          categories: self.labels,
          labels: {
            rotate: -15,
            style: { fontSize: '12px' },
          },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: [
          {
            seriesName: self.$t('AVERAGE_FUEL_CONSUMPTION'),
            axisTicks: { show: true },
            axisBorder: { show: true, color: '#FF6B35' },
            labels: {
              style: {
                fontSize: '12px',
                colors: '#FF6B35',
              },
              formatter: function (val) {
                if (Math.abs(val) >= 1e6) return (val / 1e6).toFixed(1) + 'M'
                if (Math.abs(val) >= 1e3) return (val / 1e3).toFixed(1) + 'K'
                return Number(val).toFixed(2)
              },
            },
            // title: {
            //   text: self.$t('AVERAGE_FUEL_CONSUMPTION'),
            //   style: { color: '#FF6B35' },
            //   //   offsetX: -150,
            // },
            tooltip: { enabled: true },
          },
          {
            seriesName: self.$t('COST_PER_KM'),
            opposite: true,
            axisTicks: { show: true },
            axisBorder: { show: true, color: '#9B59B6' },
            labels: {
              style: {
                fontSize: '11px',
                colors: '#9B59B6',
              },
              formatter: function (val) {
                if (Math.abs(val) >= 1e6) return (val / 1e6).toFixed(1) + 'M'
                if (Math.abs(val) >= 1e3) return (val / 1e3).toFixed(1) + 'K'
                return Number(val).toFixed(2)
              },
            },
            // title: {
            //   text: self.$t('COST_PER_KM'),
            //   style: {
            //     color: '#9B59B6',
            //     fontSize: '11px',
            //   },
            //   //   offsetX: -107,
            //   offsetY: -10,
            //   rotate: 90,
            // },
            tooltip: { enabled: true },
          },
          {
            seriesName: self.$t('TOTAL_KM'),
            opposite: true,
            axisTicks: { show: true },
            axisBorder: { show: true, color: '#4ECDC4' },
            labels: {
              style: {
                fontSize: '11px',
                colors: '#4ECDC4',
              },
              formatter: function (val) {
                if (Math.abs(val) >= 1e6) return (val / 1e6).toFixed(1) + 'M'
                if (Math.abs(val) >= 1e3) return (val / 1e3).toFixed(1) + 'K'
                return Number(val).toFixed(0)
              },
            },
            // title: {
            //   text: self.$t('TOTAL_KM'),
            //   style: {
            //     color: '#4ECDC4',
            //     fontSize: '11px',
            //   },
            //   //   offsetX: -95,
            //   rotate: 90,
            // },
            tooltip: { enabled: true },
          },
        ],
        fill: { opacity: [0.85, 0.85, 1] },
        tooltip: {
          shared: true,
          intersect: false,
          align: 'right',
          style: {
            fontSize: '12px',
            fontFamily: 'inherit',
          },
          custom: function ({ series, seriesIndex, dataPointIndex, w }) {
            var data = w.globals.initialSeries
            var labels = w.globals.labels
            var colors = w.globals.colors

            var content = `
              <div style="padding: 10px; background: white; border-radius: 6px; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1); text-align: right; min-width: 200px;">

            `

            for (let i = 0; i < series.length; i++) {
              if (series[i][dataPointIndex] !== null) {
                content += `
                  <div style="display: flex; align-items: center; margin-bottom: 4px; justify-content: flex-start; text-align: right;">
                    <div style="width: 10px; height: 10px; background-color: ${
                      colors[i]
                    }; border-radius: 2px; margin-right: 6px; flex-shrink: 0;"></div>
                    <span style="margin-right: 4px;">${data[i].name}</span>
                    <span style="margin-right: 4px;">:</span>
                    <span style="margin-right: 0; font-weight: bold;">${Number(
                      series[i][dataPointIndex]
                    ).toLocaleString('fa-IR')}</span>
                  </div>
                `
              }
            }

            content += '</div>'
            return content
          },
        },
        legend: {
          show: true,
          onItemClick: { toggleDataSeries: false },
        },
        grid: {
          borderColor: '#F3F4F6',
          xaxis: { lines: { show: false } },
          yaxis: { lines: { show: true } },
        },
        colors: ['#FF6B35', '#9B59B6', '#4ECDC4'],
      }
    },
  },
  methods: {
    showAllSeries() {
      if (!this.$refs.chart || !this.chartSeries || !this.chartSeries.length)
        return
      var names = this.chartSeries.map(function (s) {
        return s.name
      })
      var chart = this.$refs.chart.chart
      names.forEach(function (n) {
        chart.showSeries(n)
      })
    },
  },
}
</script>

<style scoped>
.chart-container {
  width: 100%;
}
.chart-container >>> .apexcharts-xaxis-label,
.chart-container >>> .apexcharts-yaxis-label {
  font-family: inherit;
}
</style>
